import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import MuiChip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import InsertChartOutlinedRoundedIcon from '@mui/icons-material/InsertChartOutlinedRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import FirstPageRoundedIcon from '@mui/icons-material/FirstPageRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';

const items = [
  {
    icon: <AccountBalanceWalletRoundedIcon />,
    title: 'Account Center',
    description:
      'Keep all your trading accounts organized in one platform, allowing you to connect and synchronize them automatically.',
    video: 'images/feat/3.webm',
  },
  {
    icon: <InsertChartOutlinedRoundedIcon />,
    title: 'Analytics',
    description:
      'Stay informed about how each of your accounts is performing with automatically generated analytics from TradesPad, based on your trades.',
    video: 'images/feat/0.webm',
  },
  {
    icon: <ChecklistRoundedIcon />,
    title: 'Trade Journaling',
    description:
      'Keep a detailed journal of your trades with the most important metrics. You’ll also see a TradingView chart with your entry and exit points in all your trades.',
    video: 'images/feat/4.webm',
  },
  {
    icon: <FirstPageRoundedIcon />,
    title: 'Backtesting (Soon)',
    description:
      'Go back in time using our trading simulator, where you can test your strategies and analyze their performance with real-time candles.',
    video: 'images/feat/1.webm',
  },
  {
    icon: <GroupsRoundedIcon />,
    title: 'Communities (Soon)',
    description:
      'Stay connected with your friends or mentors. Share your accounts, trades, and performance with your network by creating a community on TradesPad.',
    video: 'images/feat/2.webm',
  },
];

function MobileLayout({ selectedItemIndex, handleItemClick, selectedFeature }) {
  if (!items[selectedItemIndex]) {
    return null;
  }

  return (
    <Box
      sx={{
        display: { xs: 'flex', sm: 'none' },
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, overflow: 'auto' }}>
        {items.map(({ title }, index) => (
          <MuiChip
            size="medium"
            key={title + index}
            label={title}
            onClick={() => handleItemClick(index)}
            sx={{
              backgroundColor: selectedItemIndex === index ? 'white' : undefined,
              color: selectedItemIndex === index ? 'black' : undefined,
              borderColor: 'hsl(0, 0%, 20%)',
              borderWidth: 1,
              borderStyle: 'solid',
              boxSizing: 'border-box',
              transition: 'none',
              '& .MuiChip-label': {
                color: selectedItemIndex === index ? 'black' : undefined,
              },
              '&:active': {
                backgroundColor: selectedItemIndex === index ? 'white' : undefined,
                boxShadow: 'none',
              },
              '&:focus': {
                backgroundColor: selectedItemIndex === index ? 'white' : undefined,
              },
              '&:hover': {
                backgroundColor: selectedItemIndex === index ? 'white' : 'hsl(0, 0%, 10%)'
              },
            }}
          />
        ))}
      </Box>
      <Box sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'relative',
            mb: 2,
            minHeight: 280,
          }}
        >
          <video
            autoPlay
            muted
            playsInline
            loop
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            src={items[selectedItemIndex]?.video}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.8) 100%)',
            }}
          />
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography
            gutterBottom
            sx={{ color: 'text.primary', fontWeight: 'medium' }}
          >
            {selectedFeature.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            {selectedFeature.description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

MobileLayout.propTypes = {
  handleItemClick: PropTypes.func.isRequired,
  selectedFeature: PropTypes.shape({
    description: PropTypes.string.isRequired,
    icon: PropTypes.element,
    video: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  selectedItemIndex: PropTypes.number.isRequired,
};

export { MobileLayout };

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
  };

  const selectedFeature = items[selectedItemIndex];

  return (
    <Container id="features" sx={{ pt: { xs: 4, sm: 8 }, pb: { xs: 2, sm: 4 }, position: 'relative' }}>
      {/* Border line with gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100vw',
          height: '1px',
          pointerEvents: 'none',
          zIndex: 2,
        }}
      />
      {/* Background gradient */}
      <Box
        sx={(theme) => ({
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100vw',
          height: '400px',
          pointerEvents: 'none',
          zIndex: 0,
        })}
      />
      <Box sx={{ width: { sm: '100%', md: '100%' }, position: 'relative', zIndex: 1 }}>
        <Typography
          sx={{
            color: '#797979',
            fontSize: '1rem',
            textAlign: 'center',
            mt: -5,
            mb: 1,
          }}
        >
          Features
        </Typography>
        <Typography
          component="h2"
          sx={{
            color: '#FFF',
            fontWeight: 500,
            fontSize: { xs: '2rem', sm: '2.75rem' },
            textAlign: 'center',
            mb: { xs: 2, sm: 6 },
            lineHeight: 1.15,
          }}
        >
          Only useful features,
          <br />
          nothing else
        </Typography>
      </Box>
      <Box 
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row-reverse' },
          gap: 2,
        }}
      >
        <div>
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              gap: 2,
              height: '100%',
            }}
          >
            {items.map(({ icon, title, description }, index) => (
              <Box
                key={index}
                component={Button}
                onClick={() => handleItemClick(index)}
                sx={[
                  (theme) => ({
                    p: 1.5,
                    width: '100%',
                    backgroundColor: selectedItemIndex === index ? '#0B0B0B' : undefined,
                    border: selectedItemIndex === index ? '1px solid #1D1D1D' : '1px solid transparent',
                    '&:hover': {
                      backgroundColor: '#0B0B0B',
                    },
                  }),
                  selectedItemIndex === index && {
                    color: 'text.primary',
                  },
                ]}
              >
                <Box
                  sx={[
                    {
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 2,
                      textAlign: 'left',
                      textTransform: 'none',
                      color: 'text.secondary',
                    },
                    selectedItemIndex === index && {
                      color: 'text.primary',
                    },
                  ]}
                >
                  <Box sx={{ flexShrink: 0 }}>
                    {icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6">{title}</Typography>
                    <Typography variant="body2">{description}</Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
          <MobileLayout
            selectedItemIndex={selectedItemIndex}
            handleItemClick={handleItemClick}
            selectedFeature={selectedFeature}
          />
        </div>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            width: { xs: '100%', md: '100%' },
            height: 'var(--items-image-height)',
          }}
        >
          <Box
            sx={{
              height: '100%',
              width: '100%',
              display: { xs: 'none', sm: 'flex' },
              pointerEvents: 'none',
              position: 'relative',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                marginRight: 'auto',
                marginLeft: '0px',
                width: 680.5,
                height: 630.5,
                position: 'relative',
              }}
            >
              <video
                autoPlay
                muted
                playsInline
                loop
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
                src={items[selectedItemIndex]?.video}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.9) 100%)',
                  pointerEvents: 'none',
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
